import React, { useEffect, useState, useCallback, useRef } from "react";
import {
  MapContainer,
  TileLayer,
  Marker,
  Popup,
  useMap,
  ZoomControl,
} from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "./App.css";
import Login from "./Login";
import ProfileChip from "./ProfileChip";
import VehicleHistory from "./VehicleHistory";
import * as XLSX from "xlsx";
import { getUpcomingServices, ACTION_LABELS } from "./serviceSchedule";

const API = "/api/vehicles";
const POLL_INTERVAL = 15000;

// ── Brand Colors ──

const COLORS = {
  green: "#38e0cf",
  yellow: "#e0d438",
  orange: "#fd7e14",
  red: "#dc3545",
  gray: "#898989",
};

// ── Fault / Alarm decoders (from Battery CAN + Vehicle CAN DBC) ──

const VCU_FAULT_LABELS = {
  0: "No Fault",
  1: "Minor (Drivable)",
  2: "No Fault",
  3: "Power Limit",
  4: "Torque Limit",
  5: "Delayed HV Disconnect",
  6: "Immediate HV Disconnect",
};

const VCU_STRATEGY_FAULT_LABELS = {
  0: "No Fault",
  1: "Level 1 — No impact on driving",
  2: "Level 2 — Reduced performance",
  3: "Level 3 — Stop vehicle immediately",
};

const VEHICLE_STATE_LABELS = {
  0: "Normal",
  1: "Level 1 Fault — Decelerate",
  2: "Power Fault — Flashing warning",
  3: "Severe Fault — Flashing + warning",
};

const KEY_STATE_LABELS = {
  0: "OFF",
  1: "ACC",
  2: "ON",
  3: "START",
};

// ── Anomaly thresholds & alert engine ──

function getAlerts(v) {
  const alerts = [];

  // Battery
  if (v.soc != null && v.soc < 15)
    alerts.push({ level: "critical", system: "Battery", msg: `SOC critically low: ${v.soc.toFixed(1)}%` });
  else if (v.soc != null && v.soc < 25)
    alerts.push({ level: "warning", system: "Battery", msg: `SOC low: ${v.soc.toFixed(1)}%` });


  if (v.max_cell_temp != null && v.max_cell_temp > 55)
    alerts.push({ level: "critical", system: "Battery", msg: `Cell temp high: ${v.max_cell_temp}°C` });
  else if (v.max_cell_temp != null && v.max_cell_temp > 45)
    alerts.push({ level: "warning", system: "Battery", msg: `Cell temp elevated: ${v.max_cell_temp}°C` });

  if (v.avg_cell_volt != null && v.avg_cell_volt < 2.8)
    alerts.push({ level: "critical", system: "Battery", msg: `Cell voltage low: ${v.avg_cell_volt}V` });

  if (v.batt_volt != null && v.batt_volt < 500)
    alerts.push({ level: "warning", system: "Battery", msg: `Pack voltage low: ${v.batt_volt.toFixed(0)}V` });

  // Motor / MCU
  if (v.motor_temp != null && v.motor_temp > 140)
    alerts.push({ level: "critical", system: "Motor", msg: `Motor overheating: ${v.motor_temp}°C` });
  else if (v.motor_temp != null && v.motor_temp > 110)
    alerts.push({ level: "warning", system: "Motor", msg: `Motor temp high: ${v.motor_temp}°C` });

  if (v.mcu_temp != null && v.mcu_temp > 80)
    alerts.push({ level: "critical", system: "MCU", msg: `MCU overheating: ${v.mcu_temp}°C` });
  else if (v.mcu_temp != null && v.mcu_temp > 65)
    alerts.push({ level: "warning", system: "MCU", msg: `MCU temp high: ${v.mcu_temp}°C` });

  // Fault codes from CAN bus
  if (v.vcu_fault_level >= 5)
    alerts.push({ level: "critical", system: "VCU", msg: `VCU Fault: ${VCU_FAULT_LABELS[v.vcu_fault_level] || "Level " + v.vcu_fault_level}` });
  else if (v.vcu_fault_level >= 3)
    alerts.push({ level: "warning", system: "VCU", msg: `VCU Fault: ${VCU_FAULT_LABELS[v.vcu_fault_level] || "Level " + v.vcu_fault_level}` });

  if (v.vcu_strategy_fault_level >= 3)
    alerts.push({ level: "critical", system: "VCU", msg: `Strategy Fault: ${VCU_STRATEGY_FAULT_LABELS[v.vcu_strategy_fault_level]}` });
  else if (v.vcu_strategy_fault_level >= 2)
    alerts.push({ level: "warning", system: "VCU", msg: `Strategy Fault: ${VCU_STRATEGY_FAULT_LABELS[v.vcu_strategy_fault_level]}` });

  if (v.mcu_error_level >= 3)
    alerts.push({ level: "critical", system: "MCU", msg: `MCU Error Level: ${v.mcu_error_level}` });
  else if (v.mcu_error_level >= 2)
    alerts.push({ level: "warning", system: "MCU", msg: `MCU Error Level: ${v.mcu_error_level}` });

  if (v.vehicle_state >= 3)
    alerts.push({ level: "critical", system: "Vehicle", msg: VEHICLE_STATE_LABELS[v.vehicle_state] });
  else if (v.vehicle_state >= 1)
    alerts.push({ level: "warning", system: "Vehicle", msg: VEHICLE_STATE_LABELS[v.vehicle_state] });

  if (v.motor_fault === 1)
    alerts.push({ level: "warning", system: "Motor", msg: "Motor fault detected" });

  // Service schedule based on odometer
  if (v.odometer > 0) {
    const services = getUpcomingServices(v.odometer);
    const criticalServices = services.filter((s) => s.urgency === "critical");
    const warningServices = services.filter((s) => s.urgency === "warning");
    if (criticalServices.length > 0)
      alerts.push({ level: "critical", system: "Service", msg: `${criticalServices.length} service(s) overdue (<500 km)` });
    else if (warningServices.length > 0)
      alerts.push({ level: "warning", system: "Service", msg: `${warningServices.length} service(s) due soon` });
  }

  return alerts;
}

// Map marker color by-parameter ---------------------------------------------
// Each function returns one of the keys in COLORS (green / yellow / orange /
// red / gray). The map dropdown picks one of these or "auto", in which case
// `resolveColorBy(filters)` looks at the active filter to pick a sensible
// default (e.g. filter by SOC → color by SOC; filter by alert → color by
// alert severity; otherwise SOC).
function alertColor(v) {
  const a = getAlerts(v);
  if (a.some((x) => x.level === "critical")) return "red";
  if (a.some((x) => x.level === "warning")) return "yellow";
  return "green";
}

function movingColor(v) {
  const moving = (v.wheel_speed || 0) > 0 || (v.gps_speed || 0) > 0;
  return moving ? "green" : "gray";
}

function speedColor(v) {
  const s = v.wheel_speed != null ? v.wheel_speed : v.gps_speed;
  if (s == null) return "gray";
  if (s >= 40) return "green";
  if (s >= 10) return "yellow";
  if (s > 0)   return "orange";
  return "gray";
}

const VEHICLE_COLOR_BY = {
  soc:     { label: "SOC",       fn: (v) => socColor(v.soc) },
  alert:   { label: "Alerts",    fn: (v) => alertColor(v) },
  moving:  { label: "Moving",    fn: (v) => movingColor(v) },
  speed:   { label: "Speed",     fn: (v) => speedColor(v) },
};

function socColor(val) {
  if (val == null) return "gray";
  if (val >= 60) return "green";
  if (val >= 40) return "yellow";
  if (val >= 20) return "orange";
  return "red";
}

// ── Helpers ──

function fmt(val, unit = "", decimals = 1) {
  if (val == null || val === undefined) return "N/A";
  return `${Number(val).toFixed(decimals)}${unit}`;
}

function fmtTime(epoch) {
  if (!epoch) return "N/A";
  const val = Number(epoch);
  const ms = val > 1e12 ? val : val * 1000;
  return new Date(ms).toLocaleString();
}

function timeSince(epoch) {
  if (!epoch) return "N/A";
  const val = Number(epoch);
  const ms = val > 1e12 ? val : val * 1000;
  const diff = Date.now() - ms;
  if (diff < 60000) return "just now";
  if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
  if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
  return `${Math.floor(diff / 86400000)}d ago`;
}

// ── Marker icons ──

const iconCache = {};

function createMarkerIcon(color, isSelected) {
  const key = `${color}-${isSelected}`;
  if (iconCache[key]) return iconCache[key];

  const cls = isSelected ? "selected" : "";
  const bg = COLORS[color] || COLORS.gray;

  iconCache[key] = L.divIcon({
    className: "custom-marker",
    html: `<div class="marker-dot ${cls}" style="background:${bg}"></div>`,
    iconSize: [14, 14],
    iconAnchor: [7, 7],
  });

  return iconCache[key];
}

// Swap-station hexagon — same glyph as battery.eim.digital / demo.eim.digital.
let _stationIcon = null;
function stationIcon() {
  if (_stationIcon) return _stationIcon;
  _stationIcon = L.divIcon({
    className: "station-marker",
    html: `
      <div class="station-hex">
        <svg viewBox="0 0 32 32" width="28" height="28" aria-hidden="true">
          <polygon points="16,1 30,9 30,23 16,31 2,23 2,9"
                   fill="#01363F" stroke="#2dc4b3" stroke-width="2"/>
          <path d="M18 7 L11 18 L15 18 L13 25 L21 13 L17 13 Z"
                fill="#2dc4b3"/>
        </svg>
      </div>`,
    iconSize: [28, 28],
    iconAnchor: [14, 14],
  });
  return _stationIcon;
}

// ── Map fly-to ──

function MapFocus({ center }) {
  const map = useMap();
  const prevCenter = useRef(null);
  useEffect(() => {
    if (!center) return;
    const same =
      prevCenter.current &&
      prevCenter.current[0] === center[0] &&
      prevCenter.current[1] === center[1];
    if (same) return;
    prevCenter.current = center;
    map.setView(center, 18, { animate: true, duration: 0.3 });
  }, [center, map]);
  return null;
}

// ── Detail Panel ──

function DetailPanel({ vehicle: v, dataOnly = false, onClose, onViewHistory }) {
  const [tab, setTab] = useState("status");

  useEffect(() => {
    if (dataOnly && tab === "faults") setTab("status");
  }, [dataOnly, tab]);

  if (!v) return null;

  const alerts = dataOnly ? [] : getAlerts(v);
  const services =
    v.odometer > 0 ? getUpcomingServices(v.odometer) : [];
  const criticalAlerts = alerts.filter((a) => a.level === "critical");
  const warningAlerts = alerts.filter((a) => a.level === "warning");

  const tabs = dataOnly ? ["status", "service"] : ["status", "faults", "service"];

  return (
    <div className="detail-overlay">
      <div className="detail-header">
        <div>
          <h2>{v.vehicle_id}</h2>
          {(v.company_name || v.sb_vehicle_id || v.model) && (
            <div className="detail-owner">
              {v.company_name && <span>{v.company_name}</span>}
              {v.sb_vehicle_id && <span>SB: {v.sb_vehicle_id}</span>}
              {v.model && <span>{v.model}</span>}
            </div>
          )}
          {(v.battery_id || v.enneagon_plate_no || v.bcs_capacity) && (
            <div
              className="detail-owner"
              title="Battery currently reporting on this vehicle"
            >
              {v.battery_id && <span>BMS ID: {v.battery_id}</span>}
              {v.enneagon_plate_no && <span>⬢ {v.enneagon_plate_no}</span>}
              {v.bcs_capacity && <span>{v.bcs_capacity}</span>}
            </div>
          )}
          <span className="detail-subtitle">
            {v.engine === "True" ? "Engine ON" : "Engine OFF"} &middot; {timeSince(v.obd_end_time || v.gps_time)}
          </span>
          {onViewHistory && (
            <div className="detail-cross-links">
              <button className="cross-link" onClick={onViewHistory}>
                ⏱ View Full History &amp; Services
              </button>
            </div>
          )}
        </div>
        <button className="detail-close" onClick={onClose}>
          &times;
        </button>
      </div>

      {/* Alert banner */}
      {criticalAlerts.length > 0 && (
        <div className="alert-banner critical">
          {criticalAlerts.map((a, i) => (
            <div key={i} className="alert-item">
              <span className="alert-dot critical" />
              <span className="alert-system">{a.system}</span> {a.msg}
            </div>
          ))}
        </div>
      )}
      {warningAlerts.length > 0 && (
        <div className="alert-banner warning">
          {warningAlerts.map((a, i) => (
            <div key={i} className="alert-item">
              <span className="alert-dot warning" />
              <span className="alert-system">{a.system}</span> {a.msg}
            </div>
          ))}
        </div>
      )}

      {/* Tabs */}
      <div className="detail-tabs">
        {tabs.map((t) => (
          <button
            key={t}
            className={tab === t ? "active" : ""}
            onClick={() => setTab(t)}
          >
            {t === "status" ? "Status" : t === "faults" ? `Faults${alerts.length ? ` (${alerts.length})` : ""}` : `Service${services.filter(s => s.urgency !== "ok").length ? ` (${services.filter(s => s.urgency !== "ok").length})` : ""}`}
          </button>
        ))}
      </div>

      <div className="detail-body">
        {tab === "status" && (
          <>

            <div className="detail-section">
              <div className="detail-section-title">Battery</div>
              <div className="detail-grid">
                <div className="detail-item">
                  <div className="detail-item-label">BMS ID</div>
                  <div className="detail-item-value small mono">{v.battery_id || "N/A"}</div>
                </div>
                <div className="detail-item">
                  <div className="detail-item-label">Battery Serial Number</div>
                  <div className="detail-item-value small mono">{v.enneagon_plate_no || "N/A"}</div>
                </div>
                <div className="detail-item">
                  <div className="detail-item-label">BCS Capacity</div>
                  <div className="detail-item-value small">{v.bcs_capacity || "N/A"}</div>
                </div>
                <div className="detail-item">
                  <div className="detail-item-label">SOC</div>
                  <div className={`detail-item-value color-${socColor(v.soc)}`}>
                    {fmt(v.soc, "%")}
                  </div>
                </div>
                <div className="detail-item">
                  <div className="detail-item-label">Pack Voltage</div>
                  <div className="detail-item-value">{fmt(v.batt_volt, " V")}</div>
                </div>
                <div className="detail-item">
                  <div className="detail-item-label">Pack Current</div>
                  <div className="detail-item-value">{fmt(v.batt_cur, " A")}</div>
                </div>
                <div className="detail-item">
                  <div className="detail-item-label">Avg Cell Volt</div>
                  <div className="detail-item-value">{fmt(v.avg_cell_volt, " V", 2)}</div>
                </div>
                <div className="detail-item">
                  <div className="detail-item-label">Avg Cell Temp</div>
                  <div className="detail-item-value">{fmt(v.avg_cell_temp, "°C", 0)}</div>
                </div>
                <div className="detail-item">
                  <div className="detail-item-label">Max Cell Temp</div>
                  <div className="detail-item-value">{fmt(v.max_cell_temp, "°C", 0)}</div>
                </div>
                <div className="detail-item">
                  <div className="detail-item-label">Min Cell Temp</div>
                  <div className="detail-item-value">{fmt(v.min_cell_temp, "°C", 0)}</div>
                </div>
                <div className="detail-item">
                  <div className="detail-item-label">Total Charge</div>
                  <div className="detail-item-value">{fmt(v.total_chg_pwr, " kWh", 0)}</div>
                </div>
                <div className="detail-item">
                  <div className="detail-item-label">Total Discharge</div>
                  <div className="detail-item-value">{fmt(v.total_dischg_pwr, " kWh", 0)}</div>
                </div>
              </div>
            </div>

            <div className="detail-section">
              <div className="detail-section-title">Motor / MCU</div>
              <div className="detail-grid">
                <div className="detail-item">
                  <div className="detail-item-label">Motor Speed</div>
                  <div className="detail-item-value">{fmt(v.motor_speed, " rpm", 0)}</div>
                </div>
                <div className="detail-item">
                  <div className="detail-item-label">Motor Torque</div>
                  <div className="detail-item-value">{fmt(v.motor_torque, " Nm", 0)}</div>
                </div>
                <div className="detail-item">
                  <div className="detail-item-label">Motor Temp</div>
                  <div className="detail-item-value">{fmt(v.motor_temp, "°C", 0)}</div>
                </div>
                <div className="detail-item">
                  <div className="detail-item-label">MCU Temp</div>
                  <div className="detail-item-value">{fmt(v.mcu_temp, "°C", 0)}</div>
                </div>
              </div>
            </div>

            <div className="detail-section">
              <div className="detail-section-title">Vehicle</div>
              <div className="detail-grid">
                <div className="detail-item">
                  <div className="detail-item-label">Wheel Speed</div>
                  <div className="detail-item-value">{fmt(v.wheel_speed, " km/h")}</div>
                </div>
                <div className="detail-item">
                  <div className="detail-item-label">Odometer</div>
                  <div className="detail-item-value">{fmt(v.odometer, " km", 0)}</div>
                </div>
                <div className="detail-item">
                  <div className="detail-item-label">Distance to Empty</div>
                  <div className="detail-item-value">{fmt(v.ev_ttc, " km", 0)}</div>
                </div>
                <div className="detail-item">
                  <div className="detail-item-label">Accel Pedal</div>
                  <div className="detail-item-value">{fmt(v.accel_pedal, "%")}</div>
                </div>
                <div className="detail-item">
                  <div className="detail-item-label">Avg Consumption</div>
                  <div className="detail-item-value">{fmt(v.avg_consumed_power, " kWh/100km")}</div>
                </div>
                <div className="detail-item">
                  <div className="detail-item-label">Key State</div>
                  <div className="detail-item-value small">{KEY_STATE_LABELS[v.key_state] || v.key_state || "N/A"}</div>
                </div>
              </div>
            </div>

            <div className="detail-section">
              <div className="detail-section-title">GPS</div>
              <div className="detail-grid">
                <div className="detail-item">
                  <div className="detail-item-label">Location</div>
                  <div className="detail-item-value small">
                    {v.lat != null ? `${v.lat.toFixed(4)}, ${v.lng.toFixed(4)}` : "N/A"}
                  </div>
                </div>
                <div className="detail-item">
                  <div className="detail-item-label">GPS Speed</div>
                  <div className="detail-item-value">{fmt(v.gps_speed, " km/h", 0)}</div>
                </div>
                <div className="detail-item">
                  <div className="detail-item-label">Heading</div>
                  <div className="detail-item-value">{fmt(v.heading, "°", 0)}</div>
                </div>
                <div className="detail-item">
                  <div className="detail-item-label">Satellites</div>
                  <div className="detail-item-value">{v.satellites || "N/A"}</div>
                </div>
              </div>
            </div>

            <div className="detail-section">
              <div className="detail-section-title">Timestamps</div>
              <div className="detail-grid">
                <div className="detail-item">
                  <div className="detail-item-label">OBD Time</div>
                  <div className="detail-item-value small">{fmtTime(v.obd_end_time)}</div>
                </div>
                <div className="detail-item">
                  <div className="detail-item-label">GPS Time</div>
                  <div className="detail-item-value small">{fmtTime(v.gps_time)}</div>
                </div>
              </div>
            </div>
          </>
        )}

        {tab === "faults" && (
          <div className="faults-tab">
            <div className="detail-section">
              <div className="detail-section-title">Fault Codes (CAN Bus)</div>
              <div className="fault-grid">
                <div className={`fault-card ${v.vcu_fault_level >= 3 ? "fault-active" : ""}`}>
                  <div className="fault-label">VCU Fault Level</div>
                  <div className="fault-value">{v.vcu_fault_level}</div>
                  <div className="fault-desc">{VCU_FAULT_LABELS[v.vcu_fault_level] || "Unknown"}</div>
                </div>
                <div className={`fault-card ${v.vcu_strategy_fault_level >= 2 ? "fault-active" : ""}`}>
                  <div className="fault-label">VCU Strategy Fault</div>
                  <div className="fault-value">{v.vcu_strategy_fault_level}</div>
                  <div className="fault-desc">{VCU_STRATEGY_FAULT_LABELS[v.vcu_strategy_fault_level] || "Unknown"}</div>
                </div>
                <div className={`fault-card ${v.mcu_error_level >= 2 ? "fault-active" : ""}`}>
                  <div className="fault-label">MCU Error Level</div>
                  <div className="fault-value">{v.mcu_error_level}</div>
                  <div className="fault-desc">{v.mcu_error_level === 0 ? "No Error" : `Error Level ${v.mcu_error_level}`}</div>
                </div>
                <div className={`fault-card ${v.vehicle_state >= 1 ? "fault-active" : ""}`}>
                  <div className="fault-label">Vehicle State</div>
                  <div className="fault-value">{v.vehicle_state}</div>
                  <div className="fault-desc">{VEHICLE_STATE_LABELS[v.vehicle_state] || "Unknown"}</div>
                </div>
                <div className={`fault-card ${v.motor_fault === 1 ? "fault-active" : ""}`}>
                  <div className="fault-label">Motor Fault</div>
                  <div className="fault-value">{v.motor_fault}</div>
                  <div className="fault-desc">{v.motor_fault === 1 ? "Fault Detected" : "Normal"}</div>
                </div>
              </div>
            </div>

            {alerts.length === 0 && (
              <div className="no-alerts">All systems normal — no active alerts</div>
            )}

            {alerts.length > 0 && (
              <div className="detail-section">
                <div className="detail-section-title">Active Alerts ({alerts.length})</div>
                <div className="alerts-list">
                  {alerts.map((a, i) => (
                    <div key={i} className={`alert-row ${a.level}`}>
                      <span className={`alert-dot ${a.level}`} />
                      <span className="alert-system">{a.system}</span>
                      <span className="alert-msg">{a.msg}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {tab === "service" && (
          <div className="service-tab">
            {v.odometer > 0 ? (
              <>
                <div className="service-odo">
                  Odometer: <strong>{v.odometer.toLocaleString()} km</strong>
                </div>
                {services.length === 0 && (
                  <div className="no-alerts">No scheduled services found</div>
                )}
                {services
                  .filter((s) => s.urgency !== "ok")
                  .map((s, i) => (
                    <div key={i} className={`service-card ${s.urgency}`}>
                      <div className="service-card-top">
                        <span className={`service-badge ${s.urgency}`}>
                          {s.urgency === "critical"
                            ? "OVERDUE"
                            : s.urgency === "warning"
                            ? "DUE SOON"
                            : "UPCOMING"}
                        </span>
                        <span className="service-system">{s.system}</span>
                      </div>
                      <div className="service-item">{s.item}</div>
                      <div className="service-meta">
                        <span>
                          Action: <strong>{ACTION_LABELS[s.action] || s.action}</strong>
                        </span>
                        <span>
                          Next at: <strong>{s.nextServiceKm.toLocaleString()} km</strong>
                        </span>
                        <span>
                          Remaining: <strong>{s.kmRemaining.toLocaleString()} km</strong>
                        </span>
                      </div>
                      <div className="service-progress-track">
                        <div
                          className={`service-progress-fill ${s.urgency}`}
                          style={{ width: `${Math.min(100, s.progress)}%` }}
                        />
                      </div>
                      {s.remark && <div className="service-remark">{s.remark}</div>}
                    </div>
                  ))}
                {services.filter((s) => s.urgency === "ok").length > 0 && (
                  <div className="service-ok-summary">
                    {services.filter((s) => s.urgency === "ok").length} service
                    item(s) not yet due
                  </div>
                )}
              </>
            ) : (
              <div className="no-alerts">No odometer data available for service scheduling</div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Multi-select filter section ──

function FilterSection({ title, options, selected, onToggle }) {
  if (!options || options.length === 0) return null;
  return (
    <div className="filter-section">
      <div className="filter-section-title">{title}</div>
      <div className="filter-chips">
        {options.map((opt) => (
          <button
            key={opt.value}
            className={`filter-chip ${selected.has(opt.value) ? "active" : ""}`}
            onClick={() => onToggle(opt.value)}
            title={opt.label}
          >
            {opt.label}
          </button>
        ))}
      </div>
    </div>
  );
}

// ── Main App ──

function App() {
  // Shared EIM auth — httpOnly cookie at Domain=.eim.digital. Bootstrap from
  // sessionStorage to skip the auth-check flash on refresh.
  const [user, setUser] = useState(() => {
    try { return JSON.parse(sessionStorage.getItem("eim_user") || "null"); }
    catch { return null; }
  });
  const [authChecked, setAuthChecked] = useState(!!user);
  useEffect(() => {
    let cancelled = false;
    fetch("/api/auth/me", { credentials: "include" })
      .then((r) => (r.ok ? r.json() : null))
      .then((u) => {
        if (cancelled) return;
        if (u && u.email) {
          setUser(u);
          try { sessionStorage.setItem("eim_user", JSON.stringify(u)); } catch {}
        } else if (user) {
          setUser(null);
          try { sessionStorage.removeItem("eim_user"); } catch {}
        }
        setAuthChecked(true);
      })
      .catch(() => setAuthChecked(true));
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const token = user ? "cookie" : null;
  const [vehicles, setVehicles] = useState([]);
  const [swapStations, setSwapStations] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mapMode, setMapMode] = useState("satellite");
  const [mobileView, setMobileView] = useState("list");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState(() => new Set()); // alert | moving | idle
  const [fleetFilter, setFleetFilter] = useState(() => new Set());
  const [socFilter, setSocFilter] = useState(() => new Set());
  const [alertSystemFilter, setAlertSystemFilter] = useState(() => new Set());
  const [filtersOpen, setFiltersOpen] = useState(false);
  // Path-based routing: /vehicle/<id> renders the history page.
  const [route, setRoute] = useState(() =>
    typeof window === "undefined" ? "/" : window.location.pathname
  );
  useEffect(() => {
    const onPop = () => setRoute(window.location.pathname);
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);
  const navigate = useCallback((to) => {
    window.history.pushState(null, "", to);
    setRoute(to);
  }, []);
  const historyRouteMatch = route && route.toLowerCase().match(/^\/vehicle\/([^/?#]+)/);
  const historyVehicleId = historyRouteMatch ? decodeURIComponent(historyRouteMatch[1]) : null;

  const [colorBy, setColorBy] = useState(() => {
    try { return localStorage.getItem("vd_colorBy") || "auto"; } catch { return "auto"; }
  });
  useEffect(() => {
    try { localStorage.setItem("vd_colorBy", colorBy); } catch {}
  }, [colorBy]);
  const dataOnly = typeof window !== "undefined" &&
    window.location.pathname.toLowerCase().startsWith("/view");

  const toggleSet = (setter) => (val) => {
    setter((prev) => {
      const next = new Set(prev);
      if (next.has(val)) next.delete(val);
      else next.add(val);
      return next;
    });
  };

  const clearAllFilters = () => {
    setSearch("");
    setStatusFilter(new Set());
    setFleetFilter(new Set());
    setSocFilter(new Set());
    setAlertSystemFilter(new Set());
  };

  const activeFilterCount =
    (search ? 1 : 0) +
    statusFilter.size +
    fleetFilter.size +
    socFilter.size +
    alertSystemFilter.size;

  const logout = () => {
    try { fetch("/api/auth/logout", { method: "POST", credentials: "include" }); } catch {}
    try { sessionStorage.removeItem("eim_user"); } catch {}
    setUser(null);
  };

  const selectedRef = useRef(null);
  selectedRef.current = selected;

  const fetchVehicles = useCallback(async () => {
    if (!token) return;
    try {
      const res = await fetch(API, {
        credentials: "include",
      });
      if (res.status === 401) { logout(); return; }
      const data = await res.json();
      setVehicles(data);
      if (selectedRef.current) {
        const updated = data.find((v) => v.vehicle_id === selectedRef.current.vehicle_id);
        if (updated) setSelected(updated);
      }
    } catch (err) {
      console.error("Fetch failed:", err);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchVehicles();
    const interval = setInterval(fetchVehicles, POLL_INTERVAL);
    return () => clearInterval(interval);
  }, [fetchVehicles]);

  // Swap stations (SB-portal locations, geocoded server-side). Static list —
  // fetch once on mount.
  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    fetch("/api/swap-stations", { credentials: "include" })
      .then((r) => (r.ok ? r.json() : []))
      .then((d) => { if (!cancelled) setSwapStations(Array.isArray(d) ? d : []); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [token]);

  const select = (v) => {
    setSelected((prev) =>
      prev && prev.vehicle_id === v.vehicle_id ? null : v
    );
  };

  // ── Fleet stats ──

  const totalAlerts = vehicles.reduce((sum, v) => sum + (getAlerts(v).filter(a => a.level === "critical").length > 0 ? 1 : 0), 0);
  const movingCount = vehicles.filter((v) => v.wheel_speed > 0 || v.gps_speed > 0).length;

  // ── Filtering ──

  const fleetOptions = Array.from(
    new Set(vehicles.map((v) => v.company_name).filter(Boolean))
  ).sort((a, b) => a.localeCompare(b));

  const socBucket = (soc) => {
    if (soc == null) return null;
    if (soc < 15) return "critical";
    if (soc < 30) return "low";
    if (soc < 60) return "mid";
    return "good";
  };

  const statusOf = (v) => {
    const s = [];
    if (getAlerts(v).length > 0) s.push("alert");
    const moving = v.wheel_speed > 0 || v.gps_speed > 0;
    s.push(moving ? "moving" : "idle");
    return s;
  };

  const filtered = vehicles.filter((v) => {
    if (search) {
      const s = search.toLowerCase();
      const hit =
        v.vehicle_id.toLowerCase().includes(s) ||
        (v.company_name && v.company_name.toLowerCase().includes(s)) ||
        (v.sb_vehicle_id && v.sb_vehicle_id.toLowerCase().includes(s)) ||
        (v.battery_id && String(v.battery_id).toLowerCase().includes(s)) ||
        (v.enneagon_plate_no && String(v.enneagon_plate_no).toLowerCase().includes(s));
      if (!hit) return false;
    }
    if (fleetFilter.size > 0 && !fleetFilter.has(v.company_name)) return false;
    if (socFilter.size > 0 && !socFilter.has(socBucket(v.soc))) return false;
    if (statusFilter.size > 0) {
      const s = statusOf(v);
      if (!s.some((x) => statusFilter.has(x))) return false;
    }
    if (alertSystemFilter.size > 0) {
      const sys = new Set(getAlerts(v).map((a) => a.system));
      let hit = false;
      for (const s of alertSystemFilter) if (sys.has(s)) { hit = true; break; }
      if (!hit) return false;
    }
    return true;
  });

  const SOC_OPTIONS = [
    { value: "critical", label: "<15%" },
    { value: "low", label: "15–30%" },
    { value: "mid", label: "30–60%" },
    { value: "good", label: "≥60%" },
  ];

  const STATUS_OPTIONS = [
    ...(dataOnly
      ? []
      : [{ value: "alert", label: `Alerts (${vehicles.filter((v) => getAlerts(v).length > 0).length})` }]),
    { value: "moving", label: `Moving (${movingCount})` },
    { value: "idle", label: `Idle (${vehicles.length - movingCount})` },
  ];

  const alertSystemCounts = vehicles.reduce((acc, v) => {
    const seen = new Set();
    for (const a of getAlerts(v)) seen.add(a.system);
    for (const s of seen) acc[s] = (acc[s] || 0) + 1;
    return acc;
  }, {});

  const ALERT_SYSTEM_OPTIONS = ["VCU", "MCU", "Battery", "Motor", "Vehicle", "Service"]
    .filter((s) => (alertSystemCounts[s] || 0) > 0)
    .map((s) => ({ value: s, label: `${s} (${alertSystemCounts[s]})` }));

  // ── Export ──

  const exportExcel = () => {
    const rows = filtered.map((v) => ({
      "Vehicle ID": v.vehicle_id,
      "Fleet Owner": v.company_name || "",
      "SB Vehicle ID": v.sb_vehicle_id || "",
      "BMS ID": v.battery_id || "",
      "Battery Serial Number": v.enneagon_plate_no || "",
      "BCS Capacity": v.bcs_capacity || "",
      "SOC (%)": v.soc != null ? v.soc.toFixed(1) : "",
      "Pack Voltage (V)": v.batt_volt != null ? v.batt_volt.toFixed(1) : "",
      "Pack Current (A)": v.batt_cur != null ? v.batt_cur.toFixed(1) : "",
      "Motor Speed (rpm)": v.motor_speed != null ? v.motor_speed.toFixed(0) : "",
      "Motor Temp (°C)": v.motor_temp != null ? v.motor_temp.toFixed(0) : "",
      "MCU Temp (°C)": v.mcu_temp != null ? v.mcu_temp.toFixed(0) : "",
      "Max Cell Temp (°C)": v.max_cell_temp != null ? Number(v.max_cell_temp).toFixed(0) : "",
      "Min Cell Temp (°C)": v.min_cell_temp != null ? Number(v.min_cell_temp).toFixed(0) : "",
      "Wheel Speed (km/h)": v.wheel_speed != null ? v.wheel_speed.toFixed(1) : "",
      "Odometer (km)": v.odometer ? v.odometer.toFixed(0) : "",
      "VCU Fault": VCU_FAULT_LABELS[v.vcu_fault_level] || "",
      "VCU Strategy Fault": VCU_STRATEGY_FAULT_LABELS[v.vcu_strategy_fault_level] || "",
      "MCU Error": v.mcu_error_level,
      "Vehicle State": VEHICLE_STATE_LABELS[v.vehicle_state] || "",
      "Motor Fault": v.motor_fault === 1 ? "FAULT" : "OK",
      "Latitude": v.lat != null ? v.lat.toFixed(6) : "",
      "Longitude": v.lng != null ? v.lng.toFixed(6) : "",
      "Alerts": getAlerts(v).map(a => a.msg).join("; "),
      "OBD Time": v.obd_end_time ? fmtTime(v.obd_end_time) : "",
      "GPS Time": v.gps_time ? fmtTime(v.gps_time) : "",
    }));
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Vehicle Fleet");
    XLSX.writeFile(wb, `vehicle_fleet_${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  if (!authChecked) return <div className="loading">Checking session…</div>;
  if (!user) return <Login onLogin={(u) => {
    setUser(u);
    try { sessionStorage.setItem("eim_user", JSON.stringify(u)); } catch {}
  }} />;

  if (historyVehicleId) {
    return (
      <VehicleHistory
        vehicleId={historyVehicleId}
        onBack={() => navigate("/")}
      />
    );
  }

  if (loading) return <div className="loading">Loading fleet data...</div>;

  const mapCenter = (() => {
    const gps = filtered.filter((v) => v.lat != null && v.lng != null);
    if (gps.length === 0) return [20.5937, 78.9629];
    const avgLat = gps.reduce((s, v) => s + v.lat, 0) / gps.length;
    const avgLng = gps.reduce((s, v) => s + v.lng, 0) / gps.length;
    return [avgLat, avgLng];
  })();

  // Auto = follow the most-prominent active filter; explicit choice overrides.
  const effectiveColorBy = (() => {
    if (colorBy !== "auto") return colorBy;
    if (socFilter.size > 0) return "soc";
    if (alertSystemFilter.size > 0) return "alert";
    if (statusFilter.has("moving") || statusFilter.has("idle")) return "moving";
    if (statusFilter.has("alert")) return "alert";
    return "soc";
  })();
  const colorByFn = VEHICLE_COLOR_BY[effectiveColorBy]?.fn || VEHICLE_COLOR_BY.soc.fn;

  const flyTarget =
    selected && selected.lat != null
      ? [selected.lat, selected.lng]
      : null;

  return (
    <div className="dashboard">
      {/* ── Mobile Tab Bar ── */}
      <div className="mobile-tabs">
        <button className={mobileView === "list" ? "active" : ""} onClick={() => setMobileView("list")}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/><line x1="8" y1="18" x2="21" y2="18"/><line x1="3" y1="6" x2="3.01" y2="6"/><line x1="3" y1="12" x2="3.01" y2="12"/><line x1="3" y1="18" x2="3.01" y2="18"/></svg>
          Fleet
        </button>
        <button className={mobileView === "map" ? "active" : ""} onClick={() => setMobileView("map")}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polygon points="1 6 1 22 8 18 16 22 23 18 23 2 16 6 8 2 1 6"/><line x1="8" y1="2" x2="8" y2="18"/><line x1="16" y1="6" x2="16" y2="22"/></svg>
          Map
        </button>
      </div>

      {/* ── Sidebar ── */}
      <div className={`sidebar ${mobileView === "map" ? "mobile-hidden" : ""}`}>
        <div className="sidebar-header">
          <div className="sidebar-brand">
            <div className="sidebar-logo">
              <img src="/eim_logo.svg" alt="Energy in Motion" />
            </div>
            <h1>EIM <span className="brand-accent">·</span> Truck</h1>
            <ProfileChip currentPortal="truck_dashboard" onLogout={logout} compact />
            <button className="logout-btn" onClick={logout} title="Sign out">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>
            </button>
          </div>
          <div className="sidebar-stats">
            <div className="sidebar-stat"><strong>{vehicles.length}</strong> Vehicles</div>
            <div className="sidebar-stat"><strong>{movingCount}</strong> Moving</div>
            {!dataOnly && (
              <div className="sidebar-stat"><strong>{totalAlerts}</strong> Alerts</div>
            )}
            <button className="export-btn" onClick={exportExcel} title="Export to Excel">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
              Export
            </button>
          </div>
          <div className="sidebar-search">
            <input
              type="text"
              placeholder="Search plate, fleet, SB code..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <div className="filter-bar">
            <button
              className={`filter-toggle ${filtersOpen ? "open" : ""}`}
              onClick={() => setFiltersOpen((v) => !v)}
            >
              Filters{activeFilterCount > 0 ? ` · ${activeFilterCount}` : ""}
              <span className="filter-toggle-caret">{filtersOpen ? "▴" : "▾"}</span>
            </button>
            {activeFilterCount > 0 && (
              <button className="filter-clear" onClick={clearAllFilters}>
                Clear
              </button>
            )}
            <div className="filter-result-count">
              {filtered.length} / {vehicles.length}
            </div>
          </div>
          {filtersOpen && (
            <div className="filter-panel">
              <FilterSection
                title="Status"
                options={STATUS_OPTIONS}
                selected={statusFilter}
                onToggle={toggleSet(setStatusFilter)}
              />
              {!dataOnly && ALERT_SYSTEM_OPTIONS.length > 0 && (
                <FilterSection
                  title="Alert System"
                  options={ALERT_SYSTEM_OPTIONS}
                  selected={alertSystemFilter}
                  onToggle={toggleSet(setAlertSystemFilter)}
                />
              )}
              {fleetOptions.length > 0 && (
                <FilterSection
                  title="Fleet"
                  options={fleetOptions.map((f) => ({ value: f, label: f }))}
                  selected={fleetFilter}
                  onToggle={toggleSet(setFleetFilter)}
                />
              )}
              <FilterSection
                title="SOC"
                options={SOC_OPTIONS}
                selected={socFilter}
                onToggle={toggleSet(setSocFilter)}
              />
            </div>
          )}
        </div>
        <div className="vehicle-list">
          {filtered.map((v) => {
            const alerts = dataOnly ? [] : getAlerts(v);
            const hasCritical = !dataOnly && alerts.some((a) => a.level === "critical");
            return (
              <div
                key={v.vehicle_id}
                className={`vehicle-card ${selected?.vehicle_id === v.vehicle_id ? "selected" : ""} ${hasCritical ? "has-critical" : ""}`}
                onClick={() => select(v)}
              >
                <div className="vehicle-card-top">
                  <div className="vehicle-id-block">
                    <span className="vehicle-id">{v.vehicle_id}</span>
                    {v.company_name && (
                      <span className="vehicle-fleet" title={v.company_name}>
                        {v.company_name}
                        {v.sb_vehicle_id ? ` · ${v.sb_vehicle_id}` : ""}
                      </span>
                    )}
                  </div>
                  <div className="vehicle-card-badges">
                    {!dataOnly && alerts.length > 0 && (
                      <span className={`alert-count ${hasCritical ? "critical" : "warning"}`}>
                        {alerts.length}
                      </span>
                    )}
                    <span className={`vehicle-location-dot ${v.lat == null ? "no-gps" : ""}`} />
                  </div>
                </div>
                <div className="vehicle-metrics">
                  <div className="metric">
                    <div className="metric-label">SOC</div>
                    <div className={`metric-value color-${socColor(v.soc)}`}>
                      {v.soc != null ? `${v.soc.toFixed(0)}%` : "—"}
                    </div>
                    <div className="metric-bar">
                      <div className={`metric-bar-fill fill-${socColor(v.soc)}`} style={{ width: `${v.soc ?? 0}%` }} />
                    </div>
                  </div>
                  <div className="metric">
                    <div className="metric-label">Speed</div>
                    <div className="metric-value">
                      {v.wheel_speed != null ? `${v.wheel_speed.toFixed(0)}` : v.gps_speed != null ? `${v.gps_speed.toFixed(0)}` : "—"}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
          {filtered.length === 0 && (
            <div className="no-vehicles">No vehicles match filter</div>
          )}
        </div>
      </div>

      {/* ── Map ── */}
      <div className={`map-area ${mobileView === "list" ? "mobile-hidden" : ""}`}>
        <div className="map-toggle">
          <button className={mapMode === "light" ? "active" : ""} onClick={() => setMapMode("light")}>Map</button>
          <button className={mapMode === "satellite" ? "active" : ""} onClick={() => setMapMode("satellite")}>Satellite</button>
        </div>
        <div className="map-colorby" title="Marker color by">
          <label>Color by</label>
          <select value={colorBy} onChange={(e) => setColorBy(e.target.value)}>
            <option value="auto">Auto (follow filter)</option>
            <option value="soc">SOC</option>
            <option value="alert">Alerts</option>
            <option value="speed">Speed</option>
            <option value="moving">Moving / Idle</option>
          </select>
          {colorBy === "auto" && (
            <span className="map-colorby-resolved">
              {(() => {
                const k = effectiveColorBy;
                return VEHICLE_COLOR_BY[k]?.label || "SOC";
              })()}
            </span>
          )}
        </div>
        <MapContainer center={mapCenter} zoom={5} zoomControl={false}>
          <ZoomControl position="bottomright" />
          {mapMode === "light" ? (
            <TileLayer key="light" url="https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png?key=cb1_2s9p_1_0dc8e7ba31e2ef9b87fd2a2d" attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> &copy; <a href="https://carto.com/attributions">CARTO</a>' maxZoom={20} />
          ) : (
            <TileLayer key="satellite" url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}" attribution="&copy; Esri" maxZoom={19} />
          )}
          <MapFocus center={flyTarget} />
          {swapStations
            .filter((s) => s.lat != null && s.lng != null)
            .map((s) => {
              const available =
                s.total_slots != null && s.occupied_slots != null
                  ? s.total_slots - s.occupied_slots
                  : null;
              return (
                <Marker key={`station-${s.id}`} position={[s.lat, s.lng]} icon={stationIcon()}>
                  <Popup>
                    <div className="station-pop-title">⬡ {s.name}</div>
                    {s.location && <div className="station-pop-sub">{s.location}</div>}
                    {s.station_type && (
                      <div className="station-pop-row"><em>Type</em><b>{s.station_type}</b></div>
                    )}
                    {available != null && (
                      <div className="station-pop-row">
                        <em>Slots free</em><b>{available} / {s.total_slots}</b>
                      </div>
                    )}
                  </Popup>
                </Marker>
              );
            })}
          {filtered
            .filter((v) => v.lat != null && v.lng != null)
            .map((v) => (
              <Marker
                key={v.vehicle_id}
                position={[v.lat, v.lng]}
                icon={createMarkerIcon(
                  colorByFn(v),
                  selected?.vehicle_id === v.vehicle_id
                )}
                eventHandlers={{ click: () => select(v) }}
              />
            ))}
        </MapContainer>

        {selected && (
          <DetailPanel
            vehicle={selected}
            dataOnly={dataOnly}
            onClose={() => setSelected(null)}
            onViewHistory={() => navigate(`/vehicle/${encodeURIComponent(selected.vehicle_id)}`)}
          />
        )}
      </div>
    </div>
  );
}

export default App;
